// Código de barras: limpeza, dígito verificador EAN/GTIN e leitura do que o
// leitor USB bipou (código da embalagem ou etiqueta da balança).
// Usado pelo cadastro (features/produto-codigo-barras) e pelo Balcão
// (features/totem). O servidor limpa igual: backend/api/v1/products/codigo_barras.py.

export function limparCodigo(texto) {
  return String(texto || "").replace(/[\s-]/g, "").toUpperCase();
}

// Pesos 3 e 1 alternados a partir do penúltimo dígito, da direita pra esquerda.
export function digitoGtinConfere(codigo) {
  if (!/^\d{8,14}$/.test(codigo)) return false;
  const digitos = codigo.split("").map(Number);
  const informado = digitos.pop();
  const soma = digitos
    .reverse()
    .reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (soma % 10)) % 10 === informado;
}

// Etiqueta de balança (Toledo e afins, padrão do Brasil): EAN-13 que começa
// com 2 = "2" + código do produto na balança (5) + valor total em centavos (6)
// + dígito. Ex.: 2 00004 001828 5 → produto 00004, R$ 18,28. Conferido nas
// etiquetas reais da Chiquinho Carnes (29/09/2026).
export function lerEtiquetaBalanca(codigo) {
  if (!/^2\d{12}$/.test(codigo) || !digitoGtinConfere(codigo)) return null;
  return { codigoBalanca: codigo.slice(1, 6), total: Number(codigo.slice(6, 12)) / 100 };
}

const semZeros = (c) => c.replace(/^0+(?=\d)/, "");

// Produto com este código na balança: o cadastro guarda "00004" ou "4".
function produtoDaBalanca(produtos, codigoBalanca) {
  const alvo = semZeros(codigoBalanca);
  return produtos.find((p) => {
    const b = limparCodigo(p.barcode);
    return /^\d{1,6}$/.test(b) && semZeros(b) === alvo;
  });
}

/**
 * O que o leitor bipou → { tipo: "embalagem", produto }
 *                      | { tipo: "balanca", produto, total, codigoBalanca, codigo }
 *                      | { tipo: "nao_encontrado", codigo, etiqueta? }
 * O código exato da embalagem vem antes: loja que usa código interno
 * começando com 2 continua achando o produto.
 */
export function identificarLeitura(bruto, produtos) {
  const codigo = limparCodigo(bruto);
  const lista = produtos || [];
  const exato = lista.find((p) => p.barcode && limparCodigo(p.barcode) === codigo);
  if (exato) return { tipo: "embalagem", produto: exato };
  const etiqueta = lerEtiquetaBalanca(codigo);
  if (etiqueta) {
    const produto = produtoDaBalanca(lista, etiqueta.codigoBalanca);
    if (produto) return { tipo: "balanca", produto, codigo, ...etiqueta };
    return { tipo: "nao_encontrado", codigo, etiqueta };
  }
  return { tipo: "nao_encontrado", codigo };
}

/** Peso da etiqueta pelo preço do kg cadastrado (3 casas). null se não dá. */
export function pesoPelaEtiqueta(total, precoKg) {
  const preco = Number(precoKg);
  if (!(preco > 0) || !(total > 0)) return null;
  return Math.max(0.001, Math.round((total / preco) * 1000) / 1000);
}

const reais = (v) => Number(v || 0).toFixed(2).replace(".", ",");

/**
 * Campos do item do carrinho lançado pela etiqueta: `{quantity, observation,
 * etiqueta_balanca, sale_price?, final_price?}` — espalhar por cima do produto.
 * O cliente paga o valor impresso. Produto por kg e divisível entra com o peso
 * (total ÷ preço do kg); se o preço do kg não é o da balança, o preço da linha
 * é ajustado (4 casas, o máximo útil na NFC-e) para fechar no valor impresso.
 * Os outros entram como 1 unidade no valor da etiqueta. `observation` também
 * separa cada etiqueta numa linha própria do carrinho.
 */
export function itemPelaEtiqueta(produto, total, codigo) {
  const precoKg = Number(produto.final_price || produto.sale_price);
  // Peso só em produto por kg DIVISÍVEL — o servidor recusa fração nos outros.
  const peso = produto.sale_unit === "kg" && produto.is_divisible ? pesoPelaEtiqueta(total, precoKg) : null;
  if (!peso) {
    return { quantity: 1, sale_price: total, final_price: total, etiqueta_balanca: codigo, observation: `Etiqueta da balança · R$ ${reais(total)}` };
  }
  const kg = peso.toLocaleString("pt-BR", { minimumFractionDigits: 3 });
  const item = { quantity: peso, etiqueta_balanca: codigo, observation: `Balança: ${kg} kg · R$ ${reais(total)}` };
  // Meio centavo (62,055) o servidor arredonda para baixo e a JS para cima: só vale sem sobra.
  if (Math.abs(peso * precoKg - total) > 0.001) {
    const ajustado = Math.round((total / peso) * 10000) / 10000;
    item.sale_price = ajustado;
    item.final_price = ajustado;
  }
  return item;
}

/** Aviso quando a leitura não achou produto vendável nesta tela. */
export function avisoLeituraNaoEncontrada(r, todosOsProdutos, ondeVende) {
  const noCadastro = identificarLeitura(r.codigo, todosOsProdutos || []);
  if (noCadastro.produto) {
    const p = noCadastro.produto;
    return `${p.name} ${p.available === false ? "está esgotado" : `não está à venda ${ondeVende}`}`;
  }
  if (r.etiqueta) {
    return `Etiqueta da balança (R$ ${reais(r.etiqueta.total)}): nenhum produto com o código ${r.etiqueta.codigoBalanca}. Coloque esse número no Código de barras do produto.`;
  }
  return `Código ${r.codigo} não está em nenhum produto`;
}

/**
 * Busca de produto pelo código digitado (pedido do dono, 30/09/2026): com 3 ou
 * mais números, acha o produto cujo Código de barras contém esses números
 * ("7894" já mostra a Coca de 7894900011517); a etiqueta da balança digitada
 * inteira acha o produto da balança. Devolve `(produto) => boolean` para somar
 * ao filtro por nome. Usado pelas buscas do Balcão, do Novo Pedido do Delivery e
 * de Mesas.
 */
export function casaPeloCodigo(termo, produtos) {
  const t = limparCodigo(termo);
  if (!/^\d{3,}$/.test(t)) return () => false;
  const daEtiqueta = identificarLeitura(t, produtos).produto;
  return (p) => (!!p?.barcode && limparCodigo(p.barcode).includes(t)) || (!!daEtiqueta && p?.id === daEtiqueta.id);
}
